import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Save, ShieldCheck, SlidersHorizontal, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  TOOL_DEFINITIONS,
  ACCESS_FLAGS,
  DEFAULT_CONTRACT_POLICY,
  createEmptyAccessMap,
  normalizeAccessMap,
  normalizeUserAccessMap,
  normalizeTextList,
  normalizeEmailList,
  normalizeDealVisibility,
  normalizePermissionGroups,
  groupsForEmail,
  normalizeContractPolicy,
  normalizeContractPolicies,
  withFlagUpdate,
  wirksamerZugriff,
  wirksameVertragsregel,
  personenRechtGeaendert,
  personenRegelGeaendert,
  neueGruppe,
  werkzeugGruppen,
} from '../../lib/toolRechte';
import { RECHTE_TEXTE, rechtText } from '../../lib/toolRechteTexte';

const buttonStyle = {
  border: '1px solid #CBD5E1',
  borderRadius: '10px',
  padding: '9px 12px',
  background: '#FFFFFF',
  color: '#122A5C',
  fontFamily: 'Manrope, sans-serif',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
};

// Seit 08.10.2026 je Häkchen ein Satz, was es im Bereich freischaltet, und die
// Bereiche nach Menü gruppiert (toolRechteTexte.js). Häkchen ohne Wirkung stehen
// als "ohne Wirkung" da - vorher sah man nicht, welches Recht wofür gilt.
function RechtZeile({ toolKey, flag, checked, disabled, onToggle }) {
  const text = rechtText(toolKey, flag.key);
  if (text === null) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr)', gap: '7px', fontSize: '12px', color: '#94A3B8' }}>
        <span aria-hidden="true" style={{ textAlign: 'center' }}>–</span>
        <span><span style={{ fontWeight: 600 }}>{flag.label}</span> · hier ohne Wirkung</span>
      </div>
    );
  }
  return (
    <label style={{ display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr)', gap: '7px', alignItems: 'start', fontSize: '12px', color: '#475569', cursor: disabled ? 'default' : 'pointer' }}>
      <input
        type="checkbox"
        disabled={disabled}
        checked={checked}
        onChange={(event) => onToggle?.(toolKey, flag.key, event.target.checked)}
        style={{ margin: '2px 0 0' }}
      />
      <span style={{ display: 'grid', gap: '1px', minWidth: 0 }}>
        <span style={{ fontWeight: 700, color: '#122A5C' }}>{flag.label}</span>
        {text ? <span style={{ fontSize: '11.5px', lineHeight: 1.35, color: '#64748B' }}>{text}</span> : null}
      </span>
    </label>
  );
}

function PermissionGrid({ accessMap, disabled = false, onToggle }) {
  const normalizedAccessMap = normalizeAccessMap(accessMap);
  const gruppen = werkzeugGruppen();

  return (
    <div style={{ display: 'grid', gap: '12px', minWidth: 0 }}>
      {gruppen.map((gruppe) => (
        <div key={gruppe.name} style={{ display: 'grid', gap: '6px', minWidth: 0 }}>
          <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748B' }}>
            {gruppe.name}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '8px', minWidth: 0 }}>
            {gruppe.werkzeuge.map((tool) => {
              const texte = RECHTE_TEXTE[tool.key] || {};
              return (
                <div key={tool.key} style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px', background: disabled ? '#F8FAFC' : '#FFFFFF', display: 'grid', gap: '8px', alignContent: 'start', minWidth: 0 }}>
                  <div style={{ display: 'grid', gap: '2px' }}>
                    <strong style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', color: '#122A5C' }}>{tool.label}</strong>
                    {texte.ort ? <span style={{ fontSize: '11px', color: '#64748B' }}>Menü: {texte.ort}</span> : null}
                  </div>
                  <div style={{ display: 'grid', gap: '7px' }}>
                    {ACCESS_FLAGS.map((flag) => (
                      <RechtZeile
                        key={flag.key}
                        toolKey={tool.key}
                        flag={flag}
                        disabled={disabled}
                        checked={normalizedAccessMap[tool.key]?.[flag.key] === true}
                        onToggle={onToggle}
                      />
                    ))}
                  </div>
                  {texte.hinweis ? (
                    <div style={{ fontSize: '11px', lineHeight: 1.35, color: '#92400E', background: '#FFFBEB', borderRadius: '8px', padding: '6px 8px' }}>
                      {texte.hinweis}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const ohneRaender = (text) => String(text || '').trim();
const emailsAlsText = (text) => normalizeEmailList(text).join('\n');
const kennungenAlsText = (text) => normalizeTextList(text).join('\n');
const zeilenFuerListe = (text) => Math.max(3, Math.min(8, String(text || '').split('\n').length + 1));

/**
 * Eingabe mit Tipp-Puffer für Felder, deren Wert im Entwurf normalisiert wird
 * (trim, E-Mail-Listen und Kennungen getrennt, entdoppelt, sortiert). Bis
 * 24.09.2026 wurde bei jedem Tastendruck normalisiert: Die Felder verschluckten
 * Leerzeichen am Ende und Zeilenumbrüche, ein geleerter Gruppenname sprang auf
 * "Gruppe 1", und die Gruppen sortierten sich beim Tippen um.
 *
 * Deshalb steht während des Tippens nur das Getippte im Feld; normalisiert und
 * in den Entwurf übernommen wird beim Verlassen des Felds. Das deckt auch
 * "Speichern" ab: Der Klick darauf nimmt dem Feld vorher den Fokus, und der
 * Entwurf ist aktuell, bevor gespeichert wird. Gleiches Ziel wie usePuffer in
 * mobil/rechte/GruppeBausteine.jsx.
 */
function PufferEingabe({ wert, normalisiere, onUebernehmen, leerErlaubt = true, mehrzeilig = false, zeilen, ...eingabe }) {
  const [puffer, setPuffer] = useState(null);
  const anzeige = puffer === null ? wert : puffer;

  const uebernehmen = () => {
    if (puffer === null) {
      return;
    }

    setPuffer(null);
    const neu = normalisiere(puffer);
    // Leer, wo es nicht erlaubt ist (Gruppenname): nichts übernehmen, das
    // Feld zeigt wieder den bisherigen Wert.
    if ((neu === '' && !leerErlaubt) || neu === wert) {
      return;
    }

    onUebernehmen(neu);
  };

  const gemeinsam = { ...eingabe, value: anzeige, onChange: (event) => setPuffer(event.target.value), onBlur: uebernehmen };
  if (!mehrzeilig) {
    return <input type="text" {...gemeinsam} />;
  }

  // zeilen: Höhe wächst mit dem Getippten, nicht erst nach dem Übernehmen.
  return <textarea {...gemeinsam} {...(zeilen ? { rows: zeilen(anzeige) } : {})} />;
}

function SectionHeader({ icon: Icon, title, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#EEF4FF', color: '#122A5C', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
          <Icon size={17} />
        </div>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '20px', color: '#122A5C' }}>{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function ToolSettingsPage({ apiBase, session, onPermissionsChanged }) {
  const accessToken = session?.access_token || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [toolSettings, setToolSettings] = useState(null);
  const [toolUsers, setToolUsers] = useState([]);
  const [toolDefaultDraft, setToolDefaultDraft] = useState(() => createEmptyAccessMap());
  const [toolGroupDraft, setToolGroupDraft] = useState([]);
  const [toolUserDraft, setToolUserDraft] = useState({});
  const [newManagedEmail, setNewManagedEmail] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newToolEmail, setNewToolEmail] = useState('');
  const [usersNotice, setUsersNotice] = useState('');
  const [contractDefaultDraft, setContractDefaultDraft] = useState(DEFAULT_CONTRACT_POLICY);
  const [contractUserDraft, setContractUserDraft] = useState({});
  const [contractUsers, setContractUsers] = useState([]);
  const [newContractEmail, setNewContractEmail] = useState('');

  const refreshSettings = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setLoading(true);
    setUsersLoading(true);
    setUsersNotice('');

    const [contractSettingsResult, contractUsersResult] = await Promise.allSettled([
      axios.get(`${apiBase}/contracts/settings`, { headers: authHeaders }),
      axios.get(`${apiBase}/contracts/users`, { headers: authHeaders }),
    ]);

    if (contractSettingsResult.status === 'fulfilled') {
      const data = contractSettingsResult.value.data || {};
      setToolSettings(data);
      setToolDefaultDraft(normalizeAccessMap(data.toolDefaultAccess || createEmptyAccessMap()));
      setToolGroupDraft(normalizePermissionGroups(data.toolGroups || []));
      setToolUserDraft(normalizeUserAccessMap(data.toolUserAccess || {}));
      setContractDefaultDraft(normalizeContractPolicy(data.defaultPolicy));
      setContractUserDraft(normalizeContractPolicies(data.userPolicies));
    } else {
      console.error(contractSettingsResult.reason);
      toast.error(contractSettingsResult.reason?.response?.data?.detail || 'Einstellungen konnten nicht geladen werden.');
    }

    if (contractUsersResult.status === 'fulfilled') {
      const data = contractUsersResult.value.data || {};
      const users = Array.isArray(data.users) ? data.users : [];
      setToolUsers(users);
      setContractUsers(users);
      setUsersNotice(data.detail || '');
    } else {
      console.error(contractUsersResult.reason);
      setUsersNotice(contractUsersResult.reason?.response?.data?.detail || 'Nutzerliste konnte nicht geladen werden.');
      setToolUsers([]);
      setContractUsers([]);
    }

    setUsersLoading(false);
    setLoading(false);
  }, [accessToken, apiBase, authHeaders]);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const combinedToolUsers = useMemo(() => {
    const rows = new Map();

    toolUsers.forEach((user) => {
      const email = String(user.email || '').trim().toLowerCase();
      if (email) {
        rows.set(email, { ...user, email });
      }
    });

    Object.keys(toolUserDraft).forEach((email) => {
      if (!rows.has(email)) {
        rows.set(email, { id: email, email, source: 'sonderregel' });
      }
    });

    Object.keys(contractUserDraft).forEach((email) => {
      if (!rows.has(email)) {
        rows.set(email, { id: email, email, source: 'sonderregel' });
      }
    });

    toolGroupDraft.forEach((group) => {
      group.memberEmails.forEach((email) => {
        if (!rows.has(email)) {
          rows.set(email, { id: email, email, source: 'gruppe' });
        }
      });
    });

    return Array.from(rows.values()).sort((left, right) => left.email.localeCompare(right.email));
  }, [contractUserDraft, toolGroupDraft, toolUserDraft, toolUsers]);

  const combinedContractUsers = useMemo(() => {
    const rows = new Map();

    contractUsers.forEach((user) => {
      const email = String(user.email || '').trim().toLowerCase();
      if (email) {
        rows.set(email, { ...user, email });
      }
    });

    toolUsers.forEach((user) => {
      const email = String(user.email || '').trim().toLowerCase();
      if (email && !rows.has(email)) {
        rows.set(email, { ...user, email, source: user.source || 'supabase-auth' });
      }
    });

    Object.keys(contractUserDraft).forEach((email) => {
      if (!rows.has(email)) {
        rows.set(email, { id: email, email, source: 'sonderregel' });
      }
    });

    toolGroupDraft.forEach((group) => {
      group.memberEmails.forEach((email) => {
        if (!rows.has(email)) {
          rows.set(email, { id: email, email, source: 'gruppe' });
        }
      });
    });

    return Array.from(rows.values()).sort((left, right) => left.email.localeCompare(right.email));
  }, [contractUsers, contractUserDraft, toolGroupDraft, toolUsers]);

  const updateDefaultToolAccess = useCallback((toolKey, flagKey, checked) => {
    setToolDefaultDraft((current) => normalizeAccessMap({
      ...current,
      [toolKey]: withFlagUpdate(current?.[toolKey], flagKey, checked),
    }));
  }, []);

  // Hat die Person noch keine Sonderregel, beginnt die neue bei dem, was sie
  // heute wirklich darf (Standard + aktive Gruppen). Persönliche Rechte ersetzen
  // am Server Standard und Gruppen vollständig (resolveToolAccess); bis
  // 24.09.2026 begann die Regel leer, und ein einziger Schalter nahm der Person
  // still alle übrigen Rechte. Gleiche Rechnung wie am Handy (RechteSpeicher).
  const updateUserToolAccess = useCallback((email, toolKey, flagKey, checked) => {
    const geerbt = wirksamerZugriff({ standard: toolDefaultDraft, gruppen: groupsForEmail(toolGroupDraft, email) });
    setToolUserDraft((current) => personenRechtGeaendert(current, email, geerbt, toolKey, flagKey, checked));
  }, [toolDefaultDraft, toolGroupDraft]);

  const setAllUserToolAccess = useCallback((email, enabled) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      return;
    }

    const nextAccess = Object.fromEntries(
      TOOL_DEFINITIONS.map((tool) => [tool.key, { canView: enabled, canCreate: enabled, canEdit: enabled }])
    );

    setToolUserDraft((current) => ({ ...current, [normalizedEmail]: nextAccess }));
  }, []);

  const removeUserToolAccess = useCallback((email) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    setToolUserDraft((current) => {
      const next = { ...current };
      delete next[normalizedEmail];
      return next;
    });
  }, []);

  const addToolUser = useCallback(() => {
    const email = String(newToolEmail || '').trim().toLowerCase();
    if (!email) {
      return;
    }

    setToolUserDraft((current) => ({
      ...current,
      [email]: current[email] || createEmptyAccessMap(),
    }));
    setNewToolEmail('');
  }, [newToolEmail]);

  // Die Kennung entsteht aus dem Namen, und normalizePermissionGroups
  // entdoppelt nach Kennung - eine neue Gruppe mit dem Namen einer vorhandenen
  // ersetzte die vorhandene bis 24.09.2026 still. neueGruppe hängt dann -2, -3 … an.
  const addPermissionGroup = useCallback(() => {
    setToolGroupDraft((current) => neueGruppe(current, newGroupName).gruppen);
    setNewGroupName('');
  }, [newGroupName]);

  const updatePermissionGroup = useCallback((groupId, patch) => {
    setToolGroupDraft((current) => normalizePermissionGroups(
      current.map((group) => (group.id === groupId ? { ...group, ...patch } : group))
    ));
  }, []);

  const removePermissionGroup = useCallback((groupId) => {
    setToolGroupDraft((current) => current.filter((group) => group.id !== groupId));
  }, []);

  const updateGroupToolAccess = useCallback((groupId, toolKey, flagKey, checked) => {
    setToolGroupDraft((current) => normalizePermissionGroups(
      current.map((group) => {
        if (group.id !== groupId) {
          return group;
        }

        const existingAccess = normalizeAccessMap(group.access || createEmptyAccessMap());
        return {
          ...group,
          access: normalizeAccessMap({
            ...existingAccess,
            [toolKey]: withFlagUpdate(existingAccess?.[toolKey], flagKey, checked),
          }),
        };
      })
    ));
  }, []);

  const updateGroupContractPolicy = useCallback((groupId, patch) => {
    setToolGroupDraft((current) => normalizePermissionGroups(
      current.map((group) => (group.id === groupId
        ? { ...group, contractPolicy: normalizeContractPolicy({ ...group.contractPolicy, ...patch }) }
        : group))
    ));
  }, []);

  const updateGroupDealVisibility = useCallback((groupId, patch) => {
    setToolGroupDraft((current) => normalizePermissionGroups(
      current.map((group) => (group.id === groupId
        ? { ...group, dealVisibility: normalizeDealVisibility({ ...group.dealVisibility, ...patch }) }
        : group))
    ));
  }, []);

  const addManagedUser = useCallback(() => {
    const email = String(newManagedEmail || '').trim().toLowerCase();
    if (!email) {
      return;
    }

    setToolUserDraft((current) => ({
      ...current,
      [email]: current[email] || createEmptyAccessMap(),
    }));
    setContractUserDraft((current) => ({
      ...current,
      [email]: current[email] || contractDefaultDraft,
    }));
    setNewManagedEmail('');
  }, [contractDefaultDraft, newManagedEmail]);

  // Aus demselben Grund wie bei den Tool-Rechten ab der wirksamen Regel
  // (Gruppen vor Standard): Bis 24.09.2026 begann eine neue Vertragsregel beim
  // Standard, und ein Mitglied einer Gruppe mit 40 Verträgen fiel beim ersten
  // Häkchen still auf das Standardlimit zurück.
  const updateContractUserPolicy = useCallback((email, patch) => {
    const geerbt = wirksameVertragsregel({ standard: contractDefaultDraft, gruppen: groupsForEmail(toolGroupDraft, email) });
    setContractUserDraft((current) => personenRegelGeaendert(current, email, geerbt, patch));
  }, [contractDefaultDraft, toolGroupDraft]);

  const removeContractUserPolicy = useCallback((email) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    setContractUserDraft((current) => {
      const next = { ...current };
      delete next[normalizedEmail];
      return next;
    });
  }, []);

  const addContractUser = useCallback(() => {
    const email = String(newContractEmail || '').trim().toLowerCase();
    if (!email) {
      return;
    }

    setContractUserDraft((current) => ({
      ...current,
      [email]: current[email] || contractDefaultDraft,
    }));
    setNewContractEmail('');
  }, [contractDefaultDraft, newContractEmail]);

  const handleSave = useCallback(async () => {
    setSaving(true);

    try {
      const response = await axios.post(`${apiBase}/contracts/settings`, {
        defaultPolicy: contractDefaultDraft,
        userPolicies: contractUserDraft,
        toolDefaultAccess: toolDefaultDraft,
        toolGroups: toolGroupDraft,
        toolUserAccess: toolUserDraft,
      }, { headers: authHeaders });

      setToolSettings(response.data || null);
      setToolDefaultDraft(normalizeAccessMap(response.data?.toolDefaultAccess || createEmptyAccessMap()));
      setToolGroupDraft(normalizePermissionGroups(response.data?.toolGroups || []));
      setToolUserDraft(normalizeUserAccessMap(response.data?.toolUserAccess || {}));
      setContractDefaultDraft(normalizeContractPolicy(response.data?.defaultPolicy));
      setContractUserDraft(normalizeContractPolicies(response.data?.userPolicies));
      await refreshSettings();
      onPermissionsChanged?.();
      toast.success('Einstellungen gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  }, [apiBase, authHeaders, contractDefaultDraft, contractUserDraft, onPermissionsChanged, refreshSettings, toolDefaultDraft, toolGroupDraft, toolUserDraft]);

  if (!accessToken) {
    return (
      <div style={{ padding: '24px', color: '#475569', fontFamily: 'Manrope, sans-serif' }}>
        Bitte einloggen, um die Einstellungen zu verwalten.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', color: '#475569', fontFamily: 'Manrope, sans-serif' }}>
        Einstellungen werden geladen...
      </div>
    );
  }

  return (
    <div style={{ padding: '22px 24px 34px', background: '#F8FAFC', minHeight: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: '1320px', margin: '0 auto', display: 'grid', gap: '16px' }}>
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
          <SectionHeader icon={SlidersHorizontal} title="Allgemeine Einstellungen">
            <p style={{ margin: '4px 0 0', fontFamily: 'Manrope, sans-serif', fontSize: '13px', color: '#64748B' }}>
              Nicht-Admins haben ohne eigene Freigabe keinen Zugriff auf Toolbereiche.
            </p>
          </SectionHeader>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={refreshSettings} disabled={usersLoading || saving} style={{ ...buttonStyle, cursor: usersLoading || saving ? 'wait' : 'pointer' }}>
              <RefreshCw size={15} /> Aktualisieren
            </button>
            <button type="button" onClick={handleSave} disabled={saving} style={{ ...buttonStyle, border: 'none', background: '#0F766E', color: '#FFFFFF', cursor: saving ? 'wait' : 'pointer' }}>
              <Save size={15} /> {saving ? 'Speichert...' : 'Speichern'}
            </button>
          </div>
        </div>

        <section style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '14px' }}>
          <SectionHeader icon={Users} title="Nutzerrechte verwalten">
            <p style={{ margin: '4px 0 0', fontFamily: 'Manrope, sans-serif', fontSize: '13px', color: '#64748B' }}>
              E-Mail eintragen, Rechte setzen und danach speichern.
            </p>
          </SectionHeader>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input
              type="email"
              value={newManagedEmail}
              onChange={(event) => setNewManagedEmail(event.target.value)}
              placeholder="nutzer@firma.de"
              style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '10px 12px', minWidth: '280px', flex: '0 1 360px' }}
            />
            <button type="button" onClick={addManagedUser} style={buttonStyle}>
              <Users size={15} /> Nutzer hinzufügen
            </button>
          </div>
        </section>

        <section style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '14px' }}>
          <SectionHeader icon={Users} title="Nutzergruppen">
            <p style={{ margin: '4px 0 0', fontFamily: 'Manrope, sans-serif', fontSize: '13px', color: '#64748B' }}>
              Gruppen bündeln Mitglieder, Toolrechte, Vertragslimits und Deal-Sichtbarkeit. Einzelne Nutzer-Sonderregeln bleiben als Override erhalten.
            </p>
          </SectionHeader>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
              placeholder="z.B. AdVersum, Vertrieb, Partner"
              style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '10px 12px', minWidth: '280px', flex: '0 1 380px' }}
            />
            <button type="button" onClick={addPermissionGroup} style={buttonStyle}>
              <Users size={15} /> Gruppe hinzufügen
            </button>
          </div>

          {toolGroupDraft.length === 0 ? (
            <p style={{ margin: 0, fontFamily: 'Manrope, sans-serif', fontSize: '13px', color: '#64748B', background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: '12px', padding: '12px' }}>
              Noch keine Gruppen angelegt. Es gelten weiterhin Standardrechte und direkte Nutzer-Sonderregeln.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {toolGroupDraft.map((group) => {
                const policy = normalizeContractPolicy(group.contractPolicy);
                const visibility = normalizeDealVisibility(group.dealVisibility);

                return (
                  <div key={group.id} style={{ border: '1px solid #E2E8F0', borderRadius: '14px', padding: '13px', display: 'grid', gap: '12px', background: group.active ? '#FFFFFF' : '#F8FAFC' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(220px, 1fr) auto', gap: '10px', alignItems: 'start' }}>
                      <PufferEingabe
                        wert={group.name}
                        normalisiere={ohneRaender}
                        leerErlaubt={false}
                        onUebernehmen={(name) => updatePermissionGroup(group.id, { name })}
                        aria-label="Gruppenname"
                        style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', fontWeight: 700, color: '#122A5C' }}
                      />
                      <PufferEingabe
                        wert={group.description}
                        normalisiere={ohneRaender}
                        onUebernehmen={(description) => updatePermissionGroup(group.id, { description })}
                        placeholder="Beschreibung"
                        aria-label="Beschreibung"
                        style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }}
                      />
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '9px 11px' }}>
                          <input type="checkbox" checked={group.active} onChange={(event) => updatePermissionGroup(group.id, { active: event.target.checked })} />
                          Aktiv
                        </label>
                        <button type="button" onClick={() => removePermissionGroup(group.id)} style={{ ...buttonStyle, color: '#B91C1C', borderColor: '#FECACA' }}>Entfernen</button>
                      </div>
                    </div>

                    <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#475569' }}>
                      <span>Mitglieder (eine E-Mail pro Zeile)</span>
                      <PufferEingabe
                        mehrzeilig
                        wert={group.memberEmails.join('\n')}
                        normalisiere={emailsAlsText}
                        onUebernehmen={(text) => updatePermissionGroup(group.id, { memberEmails: normalizeEmailList(text) })}
                        zeilen={zeilenFuerListe}
                        style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', fontFamily: 'Manrope, sans-serif', resize: 'vertical' }}
                      />
                    </label>

                    <div style={{ display: 'grid', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '15px', color: '#122A5C' }}>Tool-Rechte</h3>
                      <PermissionGrid accessMap={group.access} onToggle={(toolKey, flagKey, checked) => updateGroupToolAccess(group.id, toolKey, flagKey, checked)} />
                    </div>

                    <div style={{ display: 'grid', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '15px', color: '#122A5C' }}>Vertragslimits</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', background: '#FBFDFF' }}>
                        <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#475569' }}>
                          <span>Erstellungen pro Monat</span>
                          <input type="number" min={1} value={policy.maxContractsPerMonth} onChange={(event) => updateGroupContractPolicy(group.id, { maxContractsPerMonth: event.target.value })} style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                        </label>
                        {[
                          ['canEditHtml', 'HTML-Bearbeitung'],
                          ['canDownloadGeneratedHtml', 'Vertrag HTML'],
                          ['canDownloadGeneratedPdf', 'Vertrag PDF'],
                          ['canDownloadOriginalHtmlTemplate', 'Original HTML'],
                          ['canDownloadOriginalPdfTemplate', 'Original PDF'],
                          ['canEditCommissions', 'Provisionen'],
                        ].map(([key, label]) => (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
                            <input type="checkbox" checked={policy[key] === true} onChange={(event) => updateGroupContractPolicy(group.id, { [key]: event.target.checked })} />
                            {label}
                          </label>
                        ))}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
                          <input type="checkbox" checked={policy.allowedTemplateVariants?.ci === true} onChange={(event) => updateGroupContractPolicy(group.id, { allowedTemplateVariants: { ...policy.allowedTemplateVariants, ci: event.target.checked } })} />
                          Normalversion
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
                          <input type="checkbox" checked={policy.allowedTemplateVariants?.druckversion === true} onChange={(event) => updateGroupContractPolicy(group.id, { allowedTemplateVariants: { ...policy.allowedTemplateVariants, druckversion: event.target.checked } })} />
                          Druckversion
                        </label>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '15px', color: '#122A5C' }}>Deal-Sichtbarkeit</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', background: '#FBFDFF' }}>
                        <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#475569' }}>
                          <span>Modus</span>
                          <select value={visibility.mode} onChange={(event) => updateGroupDealVisibility(group.id, { mode: event.target.value })} style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', background: '#FFFFFF' }}>
                            <option value="all">Alle Deals</option>
                            <option value="restricted">Nur Verantwortliche / Strukturen</option>
                          </select>
                        </label>
                        <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#475569' }}>
                          <span>Bitrix-Verantwortliche IDs</span>
                          <PufferEingabe mehrzeilig wert={visibility.bitrixUserIds.join('\n')} normalisiere={kennungenAlsText} disabled={visibility.mode !== 'restricted'} onUebernehmen={(text) => updateGroupDealVisibility(group.id, { bitrixUserIds: normalizeTextList(text) })} rows={3} style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', resize: 'vertical', background: visibility.mode === 'restricted' ? '#FFFFFF' : '#F8FAFC' }} />
                        </label>
                        <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#475569' }}>
                          <span>Bitrix-Struktur IDs</span>
                          <PufferEingabe mehrzeilig wert={visibility.departmentIds.join('\n')} normalisiere={kennungenAlsText} disabled={visibility.mode !== 'restricted'} onUebernehmen={(text) => updateGroupDealVisibility(group.id, { departmentIds: normalizeTextList(text) })} rows={3} style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', resize: 'vertical', background: visibility.mode === 'restricted' ? '#FFFFFF' : '#F8FAFC' }} />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
                          <input type="checkbox" disabled={visibility.mode !== 'restricted'} checked={visibility.includeSubDepartments} onChange={(event) => updateGroupDealVisibility(group.id, { includeSubDepartments: event.target.checked })} />
                          Unterstrukturen einschließen
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '14px' }}>
          <SectionHeader icon={ShieldCheck} title="Tool-Rechte">
            <p style={{ margin: '4px 0 0', fontFamily: 'Manrope, sans-serif', fontSize: '13px', color: '#64748B' }}>
              Unter jedem Häkchen steht, was es im Bereich freischaltet. Erstellen und Bearbeiten schalten Sehen mit ein, ohne Sehen gehen beide aus.
            </p>
          </SectionHeader>

          <div style={{ border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px', background: '#FBFDFF', display: 'grid', gap: '10px' }}>
            <div>
              <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '15px', color: '#122A5C' }}>Standard für Nicht-Admins</h3>
              <p style={{ margin: '4px 0 0', fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#64748B' }}>
                Diese Standardwerte sind initial deaktiviert und gelten nur, wenn keine eigene Sonderregel gesetzt ist.
              </p>
            </div>
            <PermissionGrid accessMap={toolDefaultDraft} onToggle={updateDefaultToolAccess} />
          </div>

          {usersNotice && (
            <p style={{ margin: 0, fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '9px 11px' }}>
              {usersNotice}
            </p>
          )}

          <div style={{ display: 'grid', gap: '10px' }}>
            {combinedToolUsers.map((user) => {
              const email = String(user.email || '').trim().toLowerCase();
              const isCurrentAdmin = user.isAdmin || email === toolSettings?.email;
              const userGroups = groupsForEmail(toolGroupDraft, email);
              // Ohne Sonderregel die wirksamen Rechte aus Standard und aktiven
              // Gruppen - genau der Stand, bei dem updateUserToolAccess die
              // neue Regel beginnen lässt (wie personSicht am Handy).
              const accessMap = wirksamerZugriff({
                istAdmin: isCurrentAdmin,
                standard: toolDefaultDraft,
                gruppen: userGroups,
                persoenlich: toolUserDraft[email] || null,
              });
              const hasCustomAccess = Boolean(toolUserDraft[email]);

              return (
                <div key={email} style={{ border: '1px solid #E2E8F0', borderRadius: '14px', padding: '12px', display: 'grid', gridTemplateColumns: 'minmax(220px, 0.7fr) minmax(420px, 2fr)', gap: '12px', background: '#FFFFFF' }}>
                  <div style={{ minWidth: 0, display: 'grid', gap: '8px', alignContent: 'start' }}>
                    <div>
                      <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', wordBreak: 'break-all' }}>{email}</div>
                      <div style={{ marginTop: '3px', fontFamily: 'Manrope, sans-serif', fontSize: '11px', color: '#64748B' }}>
                        {isCurrentAdmin ? 'Admin · immer Vollzugriff' : (hasCustomAccess ? 'Sonderregel aktiv' : (userGroups.length ? `Gruppe: ${userGroups.map((group) => group.name).join(', ')}` : 'Standardrechte'))}
                      </div>
                    </div>
                    {!isCurrentAdmin && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        <button type="button" onClick={() => setAllUserToolAccess(email, true)} style={{ ...buttonStyle, padding: '7px 9px', fontSize: '11px' }}>Alles an</button>
                        <button type="button" onClick={() => setAllUserToolAccess(email, false)} style={{ ...buttonStyle, padding: '7px 9px', fontSize: '11px' }}>Alles aus</button>
                        <button type="button" onClick={() => removeUserToolAccess(email)} disabled={!hasCustomAccess} style={{ ...buttonStyle, padding: '7px 9px', fontSize: '11px', color: hasCustomAccess ? '#B91C1C' : '#94A3B8', borderColor: hasCustomAccess ? '#FECACA' : '#E2E8F0' }}>Standard</button>
                      </div>
                    )}
                  </div>
                  <PermissionGrid accessMap={accessMap} disabled={isCurrentAdmin} onToggle={(toolKey, flagKey, checked) => updateUserToolAccess(email, toolKey, flagKey, checked)} />
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid #E2E8F0', paddingTop: '12px' }}>
            <input
              type="email"
              value={newToolEmail}
              onChange={(event) => setNewToolEmail(event.target.value)}
              placeholder="nutzer@firma.de"
              style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', minWidth: '260px' }}
            />
            <button type="button" onClick={addToolUser} style={buttonStyle}>
              <Users size={15} /> Sonderregel hinzufügen
            </button>
          </div>
        </section>

        <section style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '14px' }}>
          <SectionHeader icon={Users} title="Vermittlervertrag-Limits">
            <p style={{ margin: '4px 0 0', fontFamily: 'Manrope, sans-serif', fontSize: '13px', color: '#64748B' }}>
              Diese Werte steuern die Vertrags-Erstellung zusätzlich zur Tool-Freigabe.
            </p>
          </SectionHeader>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '10px', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px', background: '#FBFDFF' }}>
            <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#475569' }}>
              <span>Standard: Erstellungen pro Monat</span>
              <input
                type="number"
                min={1}
                value={contractDefaultDraft.maxContractsPerMonth}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({ ...current, maxContractsPerMonth: event.target.value }))}
                style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.canEditHtml}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({ ...current, canEditHtml: event.target.checked }))}
              />
              HTML-Bearbeitung erlauben
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.canDownloadGeneratedHtml}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({ ...current, canDownloadGeneratedHtml: event.target.checked }))}
              />
              Vertrag als HTML erlauben
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.canDownloadGeneratedPdf}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({ ...current, canDownloadGeneratedPdf: event.target.checked }))}
              />
              Vertrag als PDF erlauben
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.canDownloadOriginalHtmlTemplate}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({ ...current, canDownloadOriginalHtmlTemplate: event.target.checked }))}
              />
              Original-HTML erlauben
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.canDownloadOriginalPdfTemplate}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({ ...current, canDownloadOriginalPdfTemplate: event.target.checked }))}
              />
              Original-PDF erlauben
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.canEditCommissions}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({ ...current, canEditCommissions: event.target.checked }))}
              />
              Provisionen anpassen
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.allowedTemplateVariants?.ci === true}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({
                  ...current,
                  allowedTemplateVariants: { ...current.allowedTemplateVariants, ci: event.target.checked },
                }))}
              />
              Normalversion erlauben
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
              <input
                type="checkbox"
                checked={contractDefaultDraft.allowedTemplateVariants?.druckversion === true}
                onChange={(event) => setContractDefaultDraft((current) => normalizeContractPolicy({
                  ...current,
                  allowedTemplateVariants: { ...current.allowedTemplateVariants, druckversion: event.target.checked },
                }))}
              />
              Druckversion erlauben
            </label>
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            {combinedContractUsers.map((user) => {
              const email = String(user.email || '').trim().toLowerCase();
              const userGroups = groupsForEmail(toolGroupDraft, email);
              // Persönlich vor Gruppen vor Standard - derselbe Ausgangspunkt
              // wie in updateContractUserPolicy.
              const policy = wirksameVertragsregel({
                standard: contractDefaultDraft,
                gruppen: userGroups,
                persoenlich: contractUserDraft[email] || null,
              });
              const hasCustomPolicy = Boolean(contractUserDraft[email]);

              return (
                <div key={email} style={{ border: '1px solid #E2E8F0', borderRadius: '14px', padding: '11px', display: 'grid', gridTemplateColumns: 'minmax(220px, 0.8fr) 150px minmax(420px, 2fr) auto', gap: '9px', alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', fontWeight: 700, color: '#122A5C', wordBreak: 'break-all' }}>{email}</div>
                    <div style={{ marginTop: '3px', fontSize: '11px', color: '#64748B' }}>{hasCustomPolicy ? 'Sonderregel aktiv' : (userGroups.length ? `Gruppe: ${userGroups.map((group) => group.name).join(', ')}` : 'Standardlimit')}</div>
                  </div>
                  <input
                    type="number"
                    min={1}
                    value={policy.maxContractsPerMonth}
                    onChange={(event) => updateContractUserPolicy(email, { maxContractsPerMonth: event.target.value })}
                    style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 10px' }}
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input type="checkbox" checked={policy.canEditHtml} onChange={(event) => updateContractUserPolicy(email, { canEditHtml: event.target.checked })} />
                      Bearbeiten
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input type="checkbox" checked={policy.canDownloadGeneratedHtml} onChange={(event) => updateContractUserPolicy(email, { canDownloadGeneratedHtml: event.target.checked })} />
                      Vertrag HTML
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input type="checkbox" checked={policy.canDownloadGeneratedPdf} onChange={(event) => updateContractUserPolicy(email, { canDownloadGeneratedPdf: event.target.checked })} />
                      Vertrag PDF
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input type="checkbox" checked={policy.canDownloadOriginalHtmlTemplate} onChange={(event) => updateContractUserPolicy(email, { canDownloadOriginalHtmlTemplate: event.target.checked })} />
                      Original HTML
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input type="checkbox" checked={policy.canDownloadOriginalPdfTemplate} onChange={(event) => updateContractUserPolicy(email, { canDownloadOriginalPdfTemplate: event.target.checked })} />
                      Original PDF
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input type="checkbox" checked={policy.canEditCommissions} onChange={(event) => updateContractUserPolicy(email, { canEditCommissions: event.target.checked })} />
                      Provisionen
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input
                        type="checkbox"
                        checked={policy.allowedTemplateVariants?.ci === true}
                        onChange={(event) => updateContractUserPolicy(email, {
                          allowedTemplateVariants: { ...policy.allowedTemplateVariants, ci: event.target.checked },
                        })}
                      />
                      Normal
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <input
                        type="checkbox"
                        checked={policy.allowedTemplateVariants?.druckversion === true}
                        onChange={(event) => updateContractUserPolicy(email, {
                          allowedTemplateVariants: { ...policy.allowedTemplateVariants, druckversion: event.target.checked },
                        })}
                      />
                      Druck
                    </label>
                  </div>
                  <button type="button" onClick={() => removeContractUserPolicy(email)} disabled={!hasCustomPolicy} style={{ ...buttonStyle, padding: '8px 10px', color: hasCustomPolicy ? '#B91C1C' : '#94A3B8', borderColor: hasCustomPolicy ? '#FECACA' : '#E2E8F0' }}>
                    Standard
                  </button>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderTop: '1px solid #E2E8F0', paddingTop: '12px' }}>
            <input
              type="email"
              value={newContractEmail}
              onChange={(event) => setNewContractEmail(event.target.value)}
              placeholder="nutzer@firma.de"
              style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', minWidth: '260px' }}
            />
            <button type="button" onClick={addContractUser} style={buttonStyle}>
              <Users size={15} /> Vertragslimit hinzufügen
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
